// @flow
import * as React from 'react';
import {
  FollowUpsFutureQuery, FollowUpsQuery,
  PageTitle, useFollowUpsFutureQuery, useFollowUpsQuery, useSessionStorageState
} from '../../../../shared';
import { Stack } from '@mui/material';
import { TagSelector } from '../../../../features';
import {
  ClientViewSortInput,
  Field,
  PageInput,
  SortDirection
} from '@coverright/data-access/types/enrollment';
import { SearchInput } from '@coverright/ui/inputs';
import { DashboardDataGrid } from '../../ui/dashboard-data-grid';
import { useEffect } from 'react';

type Filters = {searchTerm: string, includedTagsIds: string[]};
const initialFilters: Filters = {searchTerm: '', includedTagsIds: []};

type Props = {
  title: string,
  suffix: string,
  agent?: string,
  dataSource: typeof useFollowUpsFutureQuery | typeof useFollowUpsQuery,
  excludedTagsIds?: string[],
  includedTagsIds?: string[],
}

export const FollowUps = (props: Props) => {
  const {title, dataSource, suffix, includedTagsIds = [], excludedTagsIds, agent} = props;
  const [filters, setFilters] = useFilters(suffix);
  const [sort, setSort] = useSort(suffix);
  const [page, setPage] = usePage(suffix);

  const {data: newData, previousData, loading} = dataSource({
    fetchPolicy: 'cache-and-network',
    variables: {
      agentId: agent,
      pageInput: page,
      sort,
      ...filters,
      excludedTagsIds,
      includedTagsIds: [...includedTagsIds, ...filters.includedTagsIds],
    }
  });

  useEffect(() => {
    setPage(p => ({...p, page: 0}))
    setFilters(initialFilters)
  }, [agent]);

  const data = newData || previousData;

  const clients = isTodayQuery(data) ? data?.followUps?.data : data?.followUpsFuture?.data;

  const total = isTodayQuery(data) ? data?.followUps?.totalElements : data?.followUpsFuture?.totalElements;

  return (<>
    <PageTitle title={title}>

      <Stack direction={'row'} gap={2}>
        <SearchInput value={filters.searchTerm}
                     loading={loading}
                     onChange={searchTerm => setFilters(p => ({...p, searchTerm}))} />

        <TagSelector selectedValues={filters.includedTagsIds}
                     className={'mt-0'}
                     onChange={tags => setFilters(p => ({...p, includedTagsIds: tags.map(t => t.id)}))} />
      </Stack>
    </PageTitle>

    <DashboardDataGrid page={page.page}
                       rowsPerPage={page.size}
                       total={total}
                       loading={loading}
                       sort={sort}
                       onSortModelChange={setSort}
                       onPageChange={p => setPage(prev => ({...prev, page: p }))}
                       onRowsPerPageChange={p => setPage(prev => ({page: 0, size: p }))}
                       rows={clients || []}/>

  </>)
}



const isTodayQuery = (data: FollowUpsQuery | FollowUpsFutureQuery | undefined): data is FollowUpsQuery => (
  data !== undefined &&
  'followUps' in data &&
  typeof data.followUps === 'object' &&
  data.followUps !== null &&
  Array.isArray(data.followUps.data)
)

const usePage = (suffix: string) => {
  return useSessionStorageState<PageInput>(
    'followUpsPage' + suffix,
    { page: 0, size: 20 }
  );
};



const useFilters = (suffix = '') => {
  return useSessionStorageState<Filters>(
    'followUpsFilters' + suffix,
    initialFilters
  );
};

const useSort = (suffix = '') => {
  return useSessionStorageState<ClientViewSortInput | undefined>(
    'followUpsSort' + suffix,
    {field: Field.CreatedAt, direction: SortDirection.Desc}
  )
}
